import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SelectTypeAutocomplete, ValidationInput } from 'components';
import { ErrorText, PermissionList, renderClientName, RolePermission } from 'utils';
import { EMPTY_OTHER_SIGNER, OTHER_SIGNER_OPTION_ID, otherSignerRelationshipOptions } from './constants';

const ACTION_TYPE = 'GET_CLIENT_CONTACTS';
const OTHER_SIGNER_OPTION = { id: OTHER_SIGNER_OPTION_ID };

/**
 * Picks who gave the client-side signature: one of the client's contacts, or
 * — via "+ Other Signer" — an ad-hoc person (teacher, babysitter…) entered by
 * name and role. `otherSigner` is null in contact mode and `{ name, relationship }`
 * while the Other Signer form is open. Callers without `setOtherSigner` get the
 * contact dropdown only.
 */
export const GuardianSelector = ({ setSelectedGuardian, selectedGuardian, otherSigner, setOtherSigner, showErrors }) => {
    const { clientContacts, clientContactInfo, appt } = useSelector((state) => ({
        clientContacts: state.client.clientContacts,
        clientContactInfo: state.client.clientContactInfo,
        appt: state.appointment.appointmentById,
    }));
    const clientContactList = RolePermission([PermissionList.CLIENT_CONTACT_READ?.code]) ? clientContacts : [clientContactInfo];
    const allowOtherSigner = !!setOtherSigner;
    const contactOptions = (clientContactList || []).filter((i) => i?.id);
    const options = allowOtherSigner ? [...contactOptions, OTHER_SIGNER_OPTION] : contactOptions;

    useEffect(() => {
        // Signed by an other signer: the form below takes over, so don't pre-pick a contact nobody chose.
        if (allowOtherSigner && appt?.otherSigner?.name) return;
        if (clientContactList?.length && !appt?.signorId) {
            const defaultGuard = clientContactList?.find((i) => i?.legalGuardian);
            if (defaultGuard) {
                setSelectedGuardian(defaultGuard?.id);
            }
        }
        if (appt?.signorId) {
            setSelectedGuardian(appt?.signorId);
        }
    }, [clientContactList, appt]);

    // Re-open the form with the saved values when this appointment was signed by an other signer.
    useEffect(() => {
        if (allowOtherSigner && appt?.otherSigner?.name) {
            setOtherSigner({ name: appt.otherSigner.name, relationship: appt.otherSigner.relationship });
        }
    }, [appt?.id]);

    const handleSelect = (e) => {
        if (e.target.value === OTHER_SIGNER_OPTION_ID) {
            setOtherSigner({ ...EMPTY_OTHER_SIGNER });
            return;
        }
        setSelectedGuardian(e.target.value);
    };

    const backToContacts = () => setOtherSigner(null);

    const changeOtherSigner = (e) => {
        const { name, value } = e.target;
        setOtherSigner((prev) => ({ ...(prev || EMPTY_OTHER_SIGNER), [name]: value }));
    };

    if (allowOtherSigner && otherSigner) {
        const nameError = showErrors && !otherSigner.name?.trim();
        const roleError = showErrors && !otherSigner.relationship;
        return (
            <div className="other-signer-box">
                <div className="other-signer-header">
                    <p className="other-signer-title">Other Signer</p>
                    <button type="button" className="other-signer-close" onClick={backToContacts} aria-label="Close other signer">
                        &#x2715;
                    </button>
                </div>

                <div>
                    <ValidationInput
                        variant={'outlined'}
                        name="name"
                        type={'text'}
                        label={'Name*'}
                        placeholder={"Enter signer's full name"}
                        value={otherSigner.name}
                        onChange={changeOtherSigner}
                        Length={100}
                        typeError={nameError ? ErrorText.field : ''}
                    />
                    <SelectTypeAutocomplete
                        title={'Role/Relationship*'}
                        name={'relationship'}
                        list={otherSignerRelationshipOptions}
                        defaultValue={otherSigner.relationship}
                        handleSelect={changeOtherSigner}
                        renderValue={(i) => i?.name}
                        error={roleError ? 'relationship' : ''}
                        typeError={ErrorText.field}
                        noError={!roleError}
                    />
                </div>
                <button type="button" className="other-signer-back" onClick={backToContacts}>
                    &#8592; Select from existing contacts
                </button>
            </div>
        );
    }

    return (
        <div style={{ margin: '12px 0' }}>
            <SelectTypeAutocomplete
                loadType={ACTION_TYPE}
                title={'Client Guardian'}
                name={'guardian'}
                handleSelect={handleSelect}
                defaultValue={selectedGuardian}
                list={options}
                renderValue={(i) => (i?.id === OTHER_SIGNER_OPTION_ID ? '+ Other Signer' : renderClientName(i))}
                customOptions={(i) =>
                    i?.id === OTHER_SIGNER_OPTION_ID ? (
                        <p className="other-signer-option">+ Other Signer</p>
                    ) : (
                        <p className="other-signer-contact-option">{renderClientName(i)}</p>
                    )
                }
                noError={true}
            />
        </div>
    );
};
